import React, { useState, useEffect, useRef, lazy } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Slide from '@mui/material/Slide'
import { ThemeProvider } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import cloneDeep from 'lodash/cloneDeep'
import includes from 'lodash/includes'
import { debounceSearchRender } from 'mui-datatables'
import { useDispatch, useSelector } from 'react-redux'

import type { RootState } from 'reduxStore'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import GetContentType, { CONTENT_TYPE_URL } from 'drawer/tabs/info/panels/ContentType'
import { setLayerFilter, setLayerPanelsFeatureOpen } from 'drawer/tabs/layers/actions'
import { closeTable, setTableStatus } from 'drawer/tabs/layers/table/actions'
import PoziMap from 'map/map'
import PoziZoom from 'map/zoom'
import MuiDataTableTheme from 'theme/MuiDataTableTheme'
import { linkifyText } from 'utils/url'

export const TABLE_STATUS_CLOSED = 'closed'
export const TABLE_STATUS_CLOSING = 'closing'
export const TABLE_STATUS_OPENED = 'opened'
export const TABLE_STATUS_OPENING = 'opening'

const MUIDataTable = lazy(() => import('mui-datatables'))

const styles = {
  root: {
    position: 'fixed' as const,
    width: '100%',
    right: 0,
    bottom: 0,
    zIndex: 4,
    backgroundColor: 'white'
  }
}

// Cut down interface of the table's column array to allow correct typing
interface Column {
  name: string
  label?: string
  options?: {
    filterList?: (string | number | boolean)[]
    [key: string]: any
  }
}

const LayerTable: React.FC = () => {
  // Early exit if table is not licensed for tables
  const licensed = Model.get('config.layout.drawer.tabs.layers.table.enabled', true)
  if (!licensed) {
    return null
  }

  // Get the table state from Redux
  const dispatch = useDispatch()
  const tableOpen = useSelector((state: RootState) => state.table.open)
  const tableTitleId = useSelector((state: RootState) => state.table.titleid)
  const tableLayerConf = useSelector((state: RootState) => state.layers[tableTitleId])
  const geojson = useSelector((state: RootState) => state.table.data)

  // Extract properties directly from tableLayerConf
  const layerAttributes = tableLayerConf?.attributes ?? {}
  const layerAttributesFilter = tableLayerConf?.propsFilter ?? null
  const title = tableLayerConf?.title ?? ''
  const layerParent = tableLayerConf?.parent ?? ''
  const layerDownloadable = tableLayerConf?.downloadable ?? true
  const layerSelected = tableLayerConf?.selected ?? false
  const excludedAttributes = tableLayerConf?.config?.spatial?.exclude ?? []
  const idAttribute = tableLayerConf?.config?.spatial?.id || '$id'

  // State variables for the table's properties and filtering the data
  const [idColumnIndex, setIdColumnIndex] = useState<number | string>('')
  const [columns, setColumns] = useState<any[]>([])
  const [data, setData] = useState<any[][]>([])
  const [tableKey, setTableKey] = useState<number>(0)

  // Reference variables required for cleanup when the state has changed
  const tableTitleIdRef = useRef<string | null>(null)
  const idAttributeRef = useRef<string>('')
  const layerAttributesFilterRef = useRef<any>(null)

  // Helper function to add a value to the filterList for a column in the table, which controls the filtering of the table
  const addFilterToTable = (columns: Column[], columnName: string, value: string | number): Column[] => {
    return columns.map((column) => {
      if (column.name !== columnName) {
        return {
          ...column,
          options: {
            ...column.options,
            filterList: []
          }
        }
      }

      return {
        ...column,
        options: {
          ...column.options,
          filterList: [value]
        }
      }
    })
  }

  // Helper function to remove the table filters, but keep the layer filter for the features on the map, when the table is closed or changed
  const removeTableFilters = () => {
    // Get all feature IDs from the table
    let visibleIds = data.map((row) => row[idColumnIndex as number])
    // If a layer filter has been applied in the layer control, get the feature IDs that match this filter
    if (layerAttributesFilterRef.current) {
      const filterColumnIndex = columns.findIndex((column: Column) => column.name === layerAttributesFilterRef.current.prop)
      if (filterColumnIndex !== -1) {
        visibleIds = data.filter((row) => row[filterColumnIndex] === layerAttributesFilterRef.current.value).map((row) => row[idColumnIndex as number])
      }
    }
    PoziMap().showFeaturesInLayerWithValArray(tableTitleIdRef.current, idAttributeRef.current, visibleIds)
  }

  // Callback function to customise how the table cell content is rendered
  const customBodyRender = React.useCallback((value: any) => {
    // Render 'null' and 'undefined' as empty string
    const strValue = String(value)
    if (strValue === 'null' || strValue === 'undefined') {
      return ''
    }

    // Render URLs as clickable links
    const isUrl = GetContentType(value) === CONTENT_TYPE_URL
    if (isUrl) {
      value = linkifyText(value)
    }

    return value
  }, [])

  // Callback function to handle when the user clicks on a row in the table
  const rowClicked = React.useCallback(
    (rowData: any) => {
      PoziMap().unselectAllFeatures()

      if (!(rowData instanceof Array) || rowData.length === 0) {
        console.log('LayerTable - deselected row')
        return
      }

      const idVal = rowData[idColumnIndex as number]
      const olFeature = PoziUtils.selectVectorFeatureFromIDTextAndLayerConfig(idVal, tableTitleId)
      if (!olFeature) {
        console.log('LayerTable - no feature found!', tableTitleId, idVal, idColumnIndex, rowData)
        return
      }

      const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(tableTitleId)

      // Zoom to the selected feature and keep the current zoom level if possible
      PoziZoom().zoomToFeatures([olFeature], { keepCurrentZoomWherePossible: true, olLayer })
    },
    [idColumnIndex, tableTitleId]
  )

  // Callback function to handle when the user selects a row in the table
  const rowSelected = React.useCallback(
    (...args: any[]) => {
      // 3rd argument is an array of selected row ID's. Since we only allow selecting
      // one row at a time, we're interested in the first item of that array.
      // From https://github.com/gregnb/mui-datatables
      // function(currentRowsSelected: array, allRowsSelected: array, rowsSelected: array) => void
      const rowId = args[2][0]
      const rowData = data[rowId]

      // Call the function that deals with clicked rows (not the same as selected!)
      rowClicked(rowData)
    },
    [data, rowClicked]
  )

  // Callback function for when the filter or search changes on the table
  const onTableChange = React.useCallback(
    (action: any, tableState: any) => {
      if (!['filterChange', 'resetFilters', 'search'].includes(action)) {
        return
      }

      // Get the IDs of the features that are visible in the table after the filter/search has been applied, and show these on the map
      const visibleIds = tableState.displayData.map((row: any) => data[row.dataIndex][idColumnIndex as number])
      PoziMap().showFeaturesInLayerWithValArray(tableTitleId, idAttribute, visibleIds)

      if (action === 'search') {
        return
      }

      // When the filter changes the internal tableState.filterList is updated, but this doesn't automatically update the columns,
      // which are required for us to change the filter on the table externally
      setColumns((previousColumns) =>
        previousColumns.map((column, columnIndex) => ({
          ...column,
          options: {
            ...column.options,
            filterList: tableState.filterList[columnIndex] ?? []
          }
        }))
      )

      if (action === 'resetFilters') {
        dispatch(setLayerFilter(tableTitleId, null))
        return
      }

      if (!layerAttributesFilter) {
        return
      }

      // If a filter has been applied in the layer control and it has been removed in the table, remove this filter from the layer control
      const columnIndex = tableState.columns.findIndex((column: Column) => column.name === layerAttributesFilter.prop)
      if (!tableState.filterList[columnIndex].includes(layerAttributesFilter.value)) {
        dispatch(setLayerFilter(tableTitleId, null))
      }
    },
    [data, idAttribute, idColumnIndex, layerAttributesFilter, tableTitleId]
  )

  // Callback function for when the user pushes the close button for the table
  const onClose = React.useCallback(() => {
    dispatch(setLayerPanelsFeatureOpen(tableTitleId, '')) // Roundabout way to close the table layer menu item for this layer
    dispatch(closeTable())
  }, [dispatch, tableTitleId])

  // Effect that handles when geojson data changes in Redux state (state.table.data), ie. a new layer table is opened
  useEffect(() => {
    if (!geojson || !geojson.features || geojson.features.length === 0) {
      return
    }

    const featureAttributes = cloneDeep(geojson.features[0]?.properties ?? {})
    const fullAttributeList = Object.keys(featureAttributes) as string[]

    // Remove all properties that start with $ except for $id as that is required for feature lookup
    const displayAttributeList = fullAttributeList.filter((value) => !(String(value).startsWith('$') && value !== '$id'))

    // Find the index of the id attribute column in the table
    const newIdColumnIndex = displayAttributeList.findIndex((attr) => attr === idAttribute)
    setIdColumnIndex(newIdColumnIndex)

    // Build the rows of data for the table
    const newData = []
    for (const feature of geojson.features) {
      if (!feature.properties) {
        console.debug('LayerTable - parseGeoJSON - feature without properties. Ignoring.', feature)
        continue
      }

      const dataRow = displayAttributeList.map((colName) => feature.properties?.[colName])
      newData.push(dataRow)
    }
    setData(newData)

    // Build the column list for the table
    const newColumns = []
    for (const [_index, item] of displayAttributeList.entries()) {
      // Use attribute alias as label instead of value if provided in attributes
      // NOTE: this changes the data itself and therefore, affects the download too!
      const label = layerAttributes?.[item]?.alias || item

      const columnOptions: any = {
        customBodyRender,
        display: !includes(excludedAttributes, item), // Hide column if it is in the exclude list
        filter: true,
        sort: true
      }

      // Add filterList if layer has been filtered in the layer control before opening the table
      columnOptions.filterList = []
      if (item === layerAttributesFilter?.prop) {
        columnOptions.filterList = [layerAttributesFilter?.value]
      }

      newColumns.push({
        label,
        name: item,
        options: columnOptions
      })
    }

    setColumns(newColumns)
  }, [geojson, tableOpen])

  // Effect to update the local state when the user opens the table for a layer, or switches the table to a different layer
  useEffect(() => {
    const hasTableChanged = tableTitleId !== tableTitleIdRef.current

    if (!hasTableChanged) {
      return
    }

    // If a table is opened, update the reference variables to the new table
    if (tableOpen) {
      tableTitleIdRef.current = tableTitleId
      idAttributeRef.current = idAttribute
      layerAttributesFilterRef.current = layerAttributesFilter
      setTableKey((previousKey) => previousKey + 1)
    }
  }, [tableOpen, tableTitleId])

  // Effect that handles when the user changes the filter in the layer control
  useEffect(() => {
    if (layerAttributesFilter === layerAttributesFilterRef.current) {
      return
    }

    // When the user switches the layer filter attribute, the value is set to an empty string
    // To avoid adding a filter with an empty string to the table, the attribute and value are set to undefined
    const newAttribute = layerAttributesFilter?.value === '' ? undefined : layerAttributesFilter?.prop
    const newValue = layerAttributesFilter?.value === '' ? undefined : layerAttributesFilter?.value

    // Add the new filter
    // If newAttribute is undefined, addFilterToTable will effectively remove the filter from the table
    setColumns((previousColumns) => addFilterToTable(previousColumns, newAttribute, newValue))
    if (!layerAttributesFilter) {
      const visibleIds = data.map((row) => row[idColumnIndex as number])
      PoziMap().showFeaturesInLayerWithValArray(tableTitleIdRef.current, idAttributeRef.current, visibleIds)
    }

    // Update the reference variable to the new filter
    if (tableOpen) {
      layerAttributesFilterRef.current = layerAttributesFilter
    }
  }, [layerAttributesFilter, tableOpen])

  // Table options object - memoized to prevent unnecessary re-renders
  const muiDataTableOptions: any = React.useMemo(
    () => ({
      customSearchRender: debounceSearchRender(300),
      customToolbar: () => {
        return (
          <Tooltip title="Close">
            <IconButton onClick={onClose} aria-label="close" autoFocus>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        )
      },
      download: layerDownloadable, // Don't show "Download" button if downloadable is set to false in the config
      downloadOptions: {
        filterOptions: {
          useDisplayedColumnsOnly: true,
          useDisplayedRowsOnly: true
        }
      },
      draggableColumns: {
        enabled: true
      },
      filterType: 'multiselect',
      fixedHeader: true,
      onRowClick: rowClicked, // Will not fire when row selection is enabled!
      onRowSelectionChange: rowSelected,
      onTableChange,
      pagination: true,
      print: false,
      responsive: 'standard', // 'standard', 'simple', 'vertical'
      rowsPerPage: 100, // More than 100 slows down the table significantly
      rowsPerPageOptions: [10, 20, 50, 100, 200, 500, 1000],
      selectableRows: 'single',
      selectableRowsHideCheckboxes: true,
      selectableRowsOnClick: true,
      selectToolbarPlacement: 'none', // Never show the select toolbar
      tableBodyHeight: '30vh',
      textLabels: {
        body: {
          noMatch: 'None'
        }
      }
    }),
    [layerDownloadable, rowClicked, rowSelected, onTableChange]
  )

  // console.debug('LayerTable - render', tableTitleId, layerDownloadable, layerSelected, layerParent)

  // The layer has to either be selected or be a special layer (with a parent)
  const showTable = React.useMemo(() => !!tableTitleId && tableOpen && (layerSelected || !!layerParent), [tableTitleId, tableOpen, layerSelected, layerParent])

  return (
    <Slide
      direction="up"
      in={showTable}
      onEntering={() => dispatch(setTableStatus(TABLE_STATUS_OPENING))}
      onEntered={() => dispatch(setTableStatus(TABLE_STATUS_OPENED))}
      onExiting={() => {
        dispatch(setTableStatus(TABLE_STATUS_CLOSING))
        removeTableFilters()
      }}
      onExited={() => dispatch(setTableStatus(TABLE_STATUS_CLOSED))}
    >
      <div style={styles.root}>
        <Paper elevation={1}>
          <ThemeProvider theme={MuiDataTableTheme}>
            <MUIDataTable key={tableKey} columns={columns} data={data} options={muiDataTableOptions} title={title} />
          </ThemeProvider>
        </Paper>
      </div>
    </Slide>
  )
}

export default LayerTable
